import { DateTime } from 'luxon';
import { isPlatform } from 'neuro-constants';
import { isTaskActionType } from 'neuro-schemas';
import { isNumber } from 'neuro-utils';

export const TASK_DATE_FORMAT = 'yyyy-MM-dd HH:mm:ss' as const;

/** map given js-date day into a 3-letter weekday */
const getDayString = (jsDay: number): string | undefined => {
  if (!Number.isInteger(jsDay)) return undefined;
  const map: Record<number, string> = {
    0: 'Sun',
    1: 'Mon',
    2: 'Tue',
    3: 'Wed',
    4: 'Thu',
    5: 'Fri',
    6: 'Sat',
  };
  return map[jsDay];
};

/** maps given "lähetyspäivämäärä" into a `start_inclusive` (the same date, but explicit 00:00:00) */
export const partialDateToStartInclusive = (date: PartialDate): TTaskV3['start_inclusive'] | null => {
  const dt = DateTime.fromObject({ year: date[0], month: date[1] ?? 1, day: date[2] ?? 1 }).startOf('day');
  if (!dt.isValid) return null;
  return dt.toFormat(TASK_DATE_FORMAT);
};

/** maps given `start_inclusive` into a `PartialDate`; simply just parses the date */
export const partialDateFromStartInclusive = (date: string): PartialDate | null => {
  const dt = DateTime.fromFormat(date, TASK_DATE_FORMAT);
  if (!dt.isValid) return null;
  return [dt.year, dt.month, dt.day];
};

/** maps given "määräpäivä" into an `end_exclusive` (start of next date, because the date is "exclusive") */
export const partialDateToEndExclusive = (date: PartialDate): TTaskV3['end_exclusive'] | null => {
  const dt = DateTime.fromObject({ year: date[0], month: date[1] ?? 1, day: date[2] ?? 1 })
    .plus({ days: 1 })
    .startOf('day');
  if (!dt.isValid) return null;
  return dt.toFormat(TASK_DATE_FORMAT);
};

/** maps given `end_exclusive` into a `PartialDate`;
 * subtracts 1ms (because `end_exclusive` is exclusive
 * as the name suggests) and simply just parses the date
 **/
export const partialDateFromEndExclusive = (date: string): PartialDate | null => {
  const dt = DateTime.fromFormat(date, TASK_DATE_FORMAT).minus({ milliseconds: 1 });
  if (!dt.isValid) return null;
  return [dt.year, dt.month, dt.day];
};

/** checks whether a taslist can be created or not (whether all required fields exist or not) */
export const canCreateTasklist = ({ tasklist }: { tasklist: Partial<ITaskList> | ITaskList }): boolean => {
  const { template_id, description, recurrence, start_inclusive } = tasklist;
  if (!template_id) return false;
  if (!description) return false;
  if (!recurrence) return false;
  if (!start_inclusive) return false;
  return true;
};

export const convertTaskRecurrenceToTTaskRecurrence = ({
  recurrence,
}: {
  recurrence: ITaskList['recurrence'];
}): TTaskRecurrence | null => {
  if (!recurrence.recurring) return null;
  const { duration_cal_days, every_nth, first_deadline } = recurrence;
  if (!isNumber(duration_cal_days)) return null;
  if (!isNumber(every_nth) || !Number.isInteger(every_nth)) return null;
  if (!first_deadline) return null;
  // first_deadline is "exclusive" -> subtract 1ms
  const first_dl = DateTime.fromFormat(first_deadline, TASK_DATE_FORMAT).minus({ milliseconds: 1 });
  if (!first_dl.isValid) return null;
  const dayString = getDayString(first_dl.get('weekday') % 7);
  return {
    duration_cal_days,
    every_nth,
    cron: `0 0 * * * ${dayString}`,
  };
};

/** Function for converting a tasklist of type `ITaskList` into a tasklist of type `TTaskV3` */
export const convertTasklistToTTaskV3 = ({
  tasklist,
  platform,
  orgId,
  patientId,
}: {
  tasklist: ITaskList;
  platform: Platform;
  orgId: string;
  patientId: string;
}): TTaskV3 => {
  const taskV3: TTaskV3 = {
    task_kind: tasklist.informant_id ? 'IROM' : 'PROM',
    issuer_org_id: orgId,
    patient_id: patientId,
    task_id: tasklist.id ?? '',
    informant_id: tasklist.informant_id,
    removed_at: tasklist.removed_at,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    start_inclusive: tasklist.start_inclusive ?? '',
    end_exclusive: tasklist.end_exclusive,
    recurrence: convertTaskRecurrenceToTTaskRecurrence({ recurrence: tasklist.recurrence }),
    alerts_after_start: tasklist.alerts_after_start,
    reminders_before_end: tasklist.reminders_before_end,
    actions_required: tasklist.tasks.map(t => ({ action_identifier: t, relative_priority: 1 })),
    context: {
      neuro_platform_id: platform,
      appointment_id: tasklist.appointment_id,
      template_id: tasklist.template_id ?? '',
      message_localized: tasklist.description ?? '',
    },
  };
  return taskV3;
};

export const convertTaskListToTaskV3RequestBody = ({
  tasklist,
  platform,
}: {
  tasklist: ITaskList;
  platform: Platform;
}): TTaskV3RequestBody => {
  const taskV3RequestBody: TTaskV3RequestBody = {
    ...(tasklist.informant_id ? { task_kind: 'IROM', informant_id: tasklist.informant_id } : { task_kind: 'PROM' }),
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    removed_at: null,
    start_inclusive: tasklist.start_inclusive ?? '',
    end_exclusive: tasklist.end_exclusive ?? null,
    recurrence: convertTaskRecurrenceToTTaskRecurrence({ recurrence: tasklist.recurrence }),
    alerts_after_start: tasklist.alerts_after_start ?? [],
    reminders_before_end: tasklist.reminders_before_end ?? [],
    actions_required: tasklist.tasks.map(t => ({ action_identifier: t, relative_priority: 1 })),
    context: {
      neuro_platform_id: platform,
      appointment_id: tasklist.appointment_id,
      template_id: tasklist.template_id ?? '',
      message_localized: tasklist.description ?? '',
    },
  };
  return taskV3RequestBody;
};

export const convertTTaskV3ToTasklist = ({ task }: { task: TTaskV3 }): ITaskList => {
  const tasklist: ITaskList = {
    id: task.task_id,
    task_kind: task.informant_id ? 'IROM' : 'PROM',
    template_id: task.context.template_id,
    description: task.context.message_localized,
    recurrence: task.recurrence
      ? {
          recurring: true,
          every_nth: task.recurrence.every_nth,
          // TODO: miten voi olla että jos valitsee "viikoittain" toistettavan herätteen, lähetyspäivä on kaksi viikkoa ennen. Esim. 'ninmtPreInquiry'?
          duration_cal_days: task.recurrence.duration_cal_days,
          /** This should be correct? Refer to TTaskRecurrence['duration_cal_days'] */
          first_deadline: DateTime.fromFormat(task.start_inclusive, TASK_DATE_FORMAT)
            .plus({ days: task.recurrence.duration_cal_days })
            .startOf('day')
            .toFormat(TASK_DATE_FORMAT),
        }
      : { recurring: false },
    start_inclusive: task.start_inclusive,
    end_exclusive: task.end_exclusive,
    timezone: task.timezone,
    removed_at: task.removed_at,
    alerts_after_start: task.alerts_after_start,
    reminders_before_end: task.reminders_before_end,
    tasks: task.actions_required
      .toSorted((a1, a2) => a1.relative_priority - a2.relative_priority)
      .map(a => a.action_identifier)
      .filter(a => isTaskActionType(a)),
    informant_id: task.informant_id ?? null,
    issuer_org_id: task.issuer_org_id,
    appointment_id: task.context.appointment_id ?? null,
    platform: isPlatform(task.context.neuro_platform_id) ? task.context.neuro_platform_id : undefined,
  };
  return tasklist;
};

// TODO
// - rivi 585 ish puhelinnumeron validointi yms
// - rivi 644 ish informant_id select optionformatter

export const filterActiveAndUpcomingTasks = ({ tasklists }: { tasklists: ITaskList[] }): ITaskList[] => {
  return tasklists.filter(tasklist => {
    if (!tasklist.end_exclusive) return true;
    if (DateTime.fromFormat(tasklist.end_exclusive, TASK_DATE_FORMAT).setZone(tasklist.timezone) >= DateTime.now()) {
      return true;
    }
    return false;
  });
};

export const canEditTasklist = ({ tasklist }: { tasklist: ITaskList }): boolean => {
  const { tasks, end_exclusive, timezone } = tasklist;
  /** do not allow editing treatmentEfficiency-tasks */
  if (tasks.some(task => task === 'treatmentEfficiencyBefore' || task === 'treatmentEfficiencyAfter')) {
    return false;
  }
  /** Do not allow editing if end_exclusive has passed */
  if (end_exclusive && DateTime.fromFormat(end_exclusive, TASK_DATE_FORMAT).setZone(timezone) < DateTime.now()) {
    return false;
  }
  return true;
};
